import {
  Component,
  EventEmitter,
  OnInit,
  AfterViewInit,
  OnDestroy,
  Output,
  Input,
  QueryList,
  ViewChildren,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { SearchableMultiselectComponent } from "../searchable-select/searchable-multiselect/searchable-multiselect.component";
import { FilterService } from "../../services/filter.service";
import { map, takeUntil } from "rxjs/operators";
import { forkJoin, Subject } from "rxjs";
import { SearchableSelectComponent } from "../searchable-select/searchable-select/searchable-select.component";
import { DatepickerComponent } from "../datepicker/datepicker/datepicker.component";
import { InputComponent } from "../input/input/input.component";
import { LoggingService } from "../../../logging.service";
import { CommonService } from "../../services/common.service";

@Component({
  selector: "app-filter",
  templateUrl: "./filter.component.html",
  styleUrls: ["./filter.component.scss"],
})
export class FilterComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() portfolioChange = new EventEmitter();
  @Output() subportfolioChange = new EventEmitter();
  @Output() programChange = new EventEmitter();
  @Output() resetFilter = new EventEmitter();
  @Output() portfolioFilterCleared = new EventEmitter();
  @Output() projectChange = new EventEmitter();
  @Input() autoFillHierarchy: false;
  @Input() include: string = "";
  @Input() maxVisibleRows: number = 2;
  @ViewChildren(SearchableMultiselectComponent)
  searchableMultiSelects: QueryList<SearchableMultiselectComponent>;
  @ViewChildren(SearchableSelectComponent)
  searchableSelects: QueryList<SearchableSelectComponent>;
  @ViewChildren(DatepickerComponent)
  datepickers: QueryList<DatepickerComponent>;
  @ViewChildren(InputComponent)
  inputs: QueryList<InputComponent>;
  @ViewChild("filterPillsContainer")
  filterPillsContainer: ElementRef<HTMLElement>;

  filterCfg: any;
  filterForm: any;
  isFilterhasValue = false;
  warningText = "Unexpected value";
  displayLabels: any;
  moduleName: string = '';

  exceedsTwoRows = false;
  isExpanded = false;
  isTransitioning = false;
  twoRowsHeight = 0;
  fullHeight = 0;
  hiddenFilterCount = 0;
  hiddenActiveFilterCount = 0;
  hiddenActiveFilterNames: string[] = [];

  private lastContainerWidth = 0;
  private lastPillsCount = 0;
  private resizeObserver: ResizeObserver | null = null;
  private transitionTimeout: any = null;
  private readonly unsubscribe$ = new Subject<void>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly filterService: FilterService,
    private readonly loggingService: LoggingService,
    private readonly commonService: CommonService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.getDisplayLables();
    this.filterService.filterCfgs
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: any) => {
        const prevInputs = (this.filterCfg?.inputs || [])
          .map((i: any) => i.field)
          .join(",");
        const newInputs = (res?.inputs || [])
          .map((i: any) => i.field)
          .join(",");

        this.filterCfg = res;
        if (this.filterCfg?.inputs) {
          this.isFilterhasValue = this.checkFilterHasValues();
          if (prevInputs !== newInputs) {
            this.isExpanded = false;
          }
          setTimeout(() => this.calculateRows(), 50);
        } else {
          this.isFilterhasValue = false;
          this.exceedsTwoRows = false;
        }
      });
    this.filterService.moduleContext$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((moduleName: string) => {
        this.moduleName = moduleName;
      });
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
    setTimeout(() => this.calculateRows(), 100);
  }

  setupResizeObserver(): void {
    if (
      typeof ResizeObserver !== "undefined" &&
      this.filterPillsContainer?.nativeElement
    ) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = Math.round(entry.contentRect.width);
          const currentPills =
            this.filterPillsContainer.nativeElement.querySelectorAll(".filter-pill");
          if (
            (width > 0 && Math.abs(width - this.lastContainerWidth) > 5) ||
            currentPills.length !== this.lastPillsCount
          ) {
            this.lastContainerWidth = width;
            this.lastPillsCount = currentPills.length;
            this.calculateRows();
          }
        }
      });
      this.resizeObserver.observe(this.filterPillsContainer.nativeElement);
    }
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.calculateRows();
  }

  calculateRows(): void {
    if (!this.filterPillsContainer?.nativeElement) return;
    const container = this.filterPillsContainer.nativeElement;
    const containerRect = container.getBoundingClientRect();
    const allPillNodes: HTMLElement[] = Array.from(
      container.querySelectorAll(".filter-pill")
    );
    const pills = allPillNodes.filter((p) => p.offsetHeight > 0);

    if (!pills || pills.length === 0) {
      this.exceedsTwoRows = false;
      this.twoRowsHeight = 0;
      this.fullHeight = 0;
      this.hiddenFilterCount = 0;
      this.hiddenActiveFilterCount = 0;
      this.hiddenActiveFilterNames = [];
      this.cdr.markForCheck();
      return;
    }

    // Group pills into visual rows relative to container
    const rows: {
      pills: HTMLElement[];
      indices: number[];
      top: number;
      bottom: number;
    }[] = [];

    pills.forEach((pill) => {
      const originalIndex = allPillNodes.indexOf(pill);
      const pillRect = pill.getBoundingClientRect();
      const top = Math.round(pillRect.top - containerRect.top + container.scrollTop);
      const height = Math.round(pillRect.height);
      const bottom = top + height;

      let matchedRow = rows.find((r) => Math.abs(r.top - top) <= 12);
      if (!matchedRow) {
        matchedRow = { pills: [], indices: [], top, bottom };
        rows.push(matchedRow);
      }
      matchedRow.pills.push(pill);
      matchedRow.indices.push(originalIndex);
      if (bottom > matchedRow.bottom) {
        matchedRow.bottom = bottom;
      }
    });

    // Sort rows by vertical position ascending
    rows.sort((a, b) => a.top - b.top);

    this.exceedsTwoRows = rows.length > this.maxVisibleRows;

    if (this.exceedsTwoRows) {
      const lastVisibleRowIdx = Math.max(0, this.maxVisibleRows - 1);
      const lastVisibleRowBottom = rows[lastVisibleRowIdx]?.bottom || 0;
      const nextRowTop = rows[this.maxVisibleRows]?.top;

      // Ensure the collapsed height cuts off strictly before the next row starts
      if (nextRowTop && nextRowTop > 0) {
        this.twoRowsHeight = Math.min(lastVisibleRowBottom + 4, nextRowTop - 2);
      } else {
        this.twoRowsHeight = lastVisibleRowBottom + 4;
      }

      const allBottoms = rows.map((r) => r.bottom);
      this.fullHeight = Math.max(...allBottoms) + 6;

      const hiddenRows = rows.slice(this.maxVisibleRows);
      const hiddenIndices: number[] = hiddenRows.reduce(
        (acc: number[], r: { indices: number[] }) => acc.concat(r.indices),
        []
      );
      this.hiddenFilterCount = hiddenIndices.length;

      this.updateHiddenActiveFilters(hiddenIndices);
    } else {
      this.twoRowsHeight = 0;
      this.fullHeight = 0;
      this.hiddenFilterCount = 0;
      this.hiddenActiveFilterCount = 0;
      this.hiddenActiveFilterNames = [];
    }

    this.cdr.markForCheck();
  }

  updateHiddenActiveFilters(hiddenIndices: number[]): void {
    if (!this.filterCfg?.inputs) {
      this.hiddenActiveFilterCount = 0;
      this.hiddenActiveFilterNames = [];
      return;
    }

    let count = 0;
    const names: string[] = [];

    for (const idx of hiddenIndices) {
      const input = this.filterCfg.inputs[idx];
      if (input && this.isInputActive(input)) {
        count++;
        const label = input.placeholder
          ? input.placeholder
              .replace(/^Search\s+/i, "")
              .replace(/\.\.\.$/, "")
              .trim()
          : input.field;
        names.push(label);
      }
    }

    this.hiddenActiveFilterCount = count;
    this.hiddenActiveFilterNames = names;
  }

  isInputActive(input: any): boolean {
    if (!input || input.value === undefined || input.value === null) return false;
    if (Array.isArray(input.value)) return input.value.length > 0;
    if (typeof input.value === "string") return input.value.trim() !== "";
    if (typeof input.value === "number") return true;
    if (typeof input.value === "object") return Object.keys(input.value).length > 0;
    return false;
  }

  recheckHiddenActiveFilters(): void {
    if (this.exceedsTwoRows) {
      setTimeout(() => this.calculateRows(), 30);
    }
  }

  getToggleTooltip(): string {
    if (this.isExpanded) {
      return `Collapse filters to ${this.maxVisibleRows} row${this.maxVisibleRows > 1 ? "s" : ""}`;
    }
    if (this.hiddenActiveFilterCount > 0) {
      return `Show all filters (${this.hiddenActiveFilterCount} active: ${this.hiddenActiveFilterNames.join(", ")})`;
    }
    return `Show all filters (${this.hiddenFilterCount} more)`;
  }

  getContainerMaxHeight(): string {
    if (!this.exceedsTwoRows) {
      return "none";
    }
    if (!this.isExpanded) {
      return this.twoRowsHeight ? `${this.twoRowsHeight}px` : "102px";
    }
    return this.fullHeight ? `${this.fullHeight}px` : "2000px";
  }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
    this.isTransitioning = true;
    clearTimeout(this.transitionTimeout);
    this.transitionTimeout = setTimeout(() => {
      this.isTransitioning = false;
      this.cdr.markForCheck();
    }, 320);
    this.calculateRows();
    this.cdr.markForCheck();
  }

  createFilterGroup(): FormGroup {
    return this.formBuilder.group({
      array: this.formBuilder.array([]),
      input: new FormControl(""),
    });
  }

  getPortfolios() {
    return this.filterService
      .getPortfolios()
      .pipe(map((res: any) => res.records));
  }

  getSubPortfolios(portfolioId: any = null) {
    return this.filterService.getSubPortfolios(portfolioId).pipe(
      map((res: any) => {
        if (portfolioId) {
          return res.records;
        } else {
          return res;
        }
      })
    );
  }

  getProgram(subportfolioId: any = null) {
    return this.filterService.getProgram(subportfolioId).pipe(
      map((res: any) => {
        if (subportfolioId) {
          return res.records;
        } else {
          return res;
        }
      })
    );
  }

  getProject(subportfolioId: any = null, programId: any = null) {
    return this.filterService.getProject(subportfolioId, programId).pipe(
      map((res: any) => {
        let projects = res;
        if (res.records) {
          projects = res.records;
        }
        return projects.map((project: any) => ({
          ...project,
          display_name: project.project_name,
        }));
      })
    );
  }

  getOpportunity(subportfolioId: any = null, programId: any = null) {
    return this.filterService.getOpportunity(subportfolioId, programId).pipe(
      map((res: any) => {
        let opportunities = res;
        if (res.records) {
          opportunities = res.records;
        }
        return opportunities.map((opportunity: any) => ({
          ...opportunity,
          display_name: opportunity.opportunity_name,
        }));
      })
    );
  }

  hasValue(val: any) {
    if (Array.isArray(val)) {
      if (val.length) {
        return true;
      } else {
        return false;
      }
    } else if (val) {
      return true;
    } else {
      this.loggingService.warn(this.warningText);
    }
    return false;
  }

  getActiveFields() {
    let hasValue = false;
    let fields: any[] = [];
    for (const item of this.filterCfg.inputs) {
      hasValue = this.hasValue(item.value);
      if (hasValue) {
        fields.push(item);
      }
    }
    return fields;
  }

  checkFilterHasValues() {
    let hasValue = false;
    for (const item of this.filterCfg.inputs) {
      hasValue = this.hasValue(item.value);
      if (hasValue) {
        break;
      }
    }
    return hasValue;
  }

  clearFilter(field: string) {
    const cfg = this.filterCfg.inputs.find((ele: any) => ele.field === field);
    if (cfg) {
      if (cfg.type !== "searchable-multiselect") {
        cfg.value = null;
        this.manipulateSearchableSelectWithoutEmitting(null, field);
      } else {
        cfg.value = [];
        this.manipulateSearchableMultiSelectWithoutEmitting([], field);
      }
    }
  }

  clearFilters(fields: string[]) {
    fields.forEach((field) => this.clearFilter(field));
  }

  changeOptionsAndSetValues(
    fieldsToChange: string[],
    options: any,
    filterFieldsCfgs: any
  ) {
    let fieldtoChange = "";
    for (let i = 0; i < filterFieldsCfgs.length; i++) {
      fieldtoChange = fieldsToChange[i];
      filterFieldsCfgs[i].options = options[fieldtoChange];
      if (fieldtoChange === "portfolio") {
        if (filterFieldsCfgs[i].type === "searchable-multiselect") {
        } else {
          const new_val = options[fieldtoChange][0] || "";
          filterFieldsCfgs[i].value = new_val;
          this.manipulateSearchableSelectWithoutEmitting(
            new_val || "",
            fieldtoChange
          );
        }
      }
      if (fieldtoChange === "sub_portfolio") {
        if (filterFieldsCfgs[i].type === "searchable-multiselect") {
        } else {
          const new_val = options[fieldtoChange][0] || "";
          filterFieldsCfgs[i].value = new_val;
          this.manipulateSearchableSelectWithoutEmitting(
            new_val,
            "subportfolio"
          );
        }
      }
      if (fieldtoChange === "program") {
        if (filterFieldsCfgs[i].type === "searchable-multiselect") {
        } else {
          const new_val = options[fieldtoChange][0] || "";
          filterFieldsCfgs[i].value = new_val;
          this.manipulateSearchableSelectWithoutEmitting(
            new_val,
            fieldtoChange
          );
        }
      }
    }
  }

  modifyFieldsToChange(fieldsToChange: string[], filterFieldsCfgs: any[]) {
    const projectFieldCfg = filterFieldsCfgs.find(
      (ele: any) => ele.field === "project"
    );
    const opportunityFieldCfg = filterFieldsCfgs.find(
      (ele: any) => ele.field === "opportunity"
    );
    if (projectFieldCfg) {
      fieldsToChange.push("projects");
    }
    if (opportunityFieldCfg) {
      fieldsToChange.push("opportunities");
    }
    return fieldsToChange;
  }

  resetOptions(remainingFilterCfgs: any) {
    // Preserve any active values for the hierarchy so they survive the reset
    const activeFields = this.getActiveFields();
    const prevValues: any = {};
    for (const item of activeFields) {
      // store previous value by field name
      prevValues[item.field] = item.value;
    }
    const searchableMultiSelect = "searchable-multiselect";

    // Only include `project` in the hierarchy if the current filter config had it
    const hasProject = this.filterCfg?.inputs?.some(
      (ele: any) => ele.field === "project"
    );
    // Only include `opportunity` in the hierarchy if the current filter config had it
    const hasOpportunity = this.filterCfg?.inputs?.some(
      (ele: any) => ele.field === "opportunity"
    );

    const calls: any = {
      portfolios: this.getPortfolios(),
      subportfolios: this.getSubPortfolios(),
      programs: this.getProgram(),
    };
    if (hasProject) calls.projects = this.getProject();
    if (hasOpportunity) calls.opportunities = this.getOpportunity();

    forkJoin(calls).subscribe((resp: any) => {
      const portfolios = resp.portfolios;
      const subportfolios = resp.subportfolios;
      const programs = resp.programs;
      const projects = resp.projects || [];
      const opportunities = resp.opportunities || [];

      const inputs: any[] = [
        {
          field: "portfolio",
          type: searchableMultiSelect,
          placeholder: `Search ${this.displayLabels.portfolio}...`,
          options: portfolios,
          value: [],
        },
        {
          field: "subportfolio",
          type: searchableMultiSelect,
          placeholder: `Search ${this.displayLabels.sub_portfolio}...`,
          options: subportfolios,
          value: [],
        },
        {
          field: "program",
          type: searchableMultiSelect,
          placeholder: `Search ${this.displayLabels.program}...`,
          options: programs,
          value: [],
        },
      ];
      if (hasProject) {
        inputs.push({
          field: "project",
          type: searchableMultiSelect,
          placeholder: `Search ${this.displayLabels.project}...`,
          options: projects,
          value: [],
        });
      }
      if (hasOpportunity) {
        inputs.push({
          field: "opportunity",
          type: searchableMultiSelect,
          placeholder: `Search ${
            this.displayLabels.opportunity || "Opportunity"
          }...`,
          options: opportunities,
          value: [],
        });
      }

      this.filterCfg = { inputs };

      // If any of the hierarchy fields had previous values, restore them
      const hierarchyFields = ["portfolio", "subportfolio", "program"];
      if (hasProject) hierarchyFields.push("project");
      if (hasOpportunity) hierarchyFields.push("opportunity");

      for (const field of hierarchyFields) {
        const prev = prevValues[field];
        if (this.hasValue(prev)) {
          const normalizedPrev = Array.isArray(prev)
            ? prev
            : prev
              ? [prev]
              : [];
          const cfg = this.filterCfg.inputs.find(
            (ele: any) => ele.field === field
          );
          if (cfg) {
            // Restore as full option objects when possible so display_name is available
            const restored = normalizedPrev.map((p: any) => {
              if (p && typeof p === "object" && (p.display_name || p.name))
                return p;
              const pid = p && typeof p === "object" ? p.id : p;
              const match = (cfg.options || []).find((o: any) => o.id === pid);
              return match || p;
            });
            cfg.value = restored;
          }
        }
      }

      this.filterCfg.inputs.push(...remainingFilterCfgs);
      this.filterService.filterCfgs.next(this.filterCfg);

      // After Angular has applied the new inputs/options to child components,
      // set their internal selected values without emitting so chips render.
      setTimeout(() => {
        for (const field of hierarchyFields) {
          const cfg = this.filterCfg.inputs.find(
            (ele: any) => ele.field === field
          );
          if (!cfg) continue;
          const val = cfg.value && cfg.value.length ? cfg.value : [];
          this.manipulateSearchableMultiSelectWithoutEmitting(val, field);
        }
        this.filterService.setFilterValue(this.filterCfg);
      }, 50);
    });
  }

  onPortfolioChange(
    value: any,
    field: string,
    filterFieldsCfgs: any,
    include?: string,
    isMultiSelect?: boolean
  ) {
    const ids = (value ? (Array.isArray(value) ? value : [value]) : [])
      .filter((item) => item?.id !== null && item?.id !== undefined)
      .map((item) => item.id);
    if (ids.length > 0) {
      this.filterService
        .getFillOptions(`portfolio_id=${ids}`, include, this.moduleName)
        .subscribe((options: any) => {
          const fieldsToChange = this.modifyFieldsToChange(
            ["sub_portfolios", "programs"],
            filterFieldsCfgs
          );
          this.changeOptionsAndSetValues(
            fieldsToChange,
            options,
            filterFieldsCfgs
          );
          if (!isMultiSelect)
            this.clearFilters(["project", "program", "subportfolio"]);
          this.filterService.setFilterValue(this.filterCfg);
        });
    } else {
      this.isFilterhasValue = this.checkFilterHasValues();
      if (this.isFilterhasValue) {
        const fieldsToExclude = [
          "portfolio",
          "subportfolio",
          "program",
          "project",
          "opportunity",
        ];
        const remainingFilterCfgs = this.filterCfg.inputs.filter((ele: any) => {
          return !fieldsToExclude.includes(ele.field);
        });
        if (isMultiSelect) {
          this.resetOptions(remainingFilterCfgs);
          return;
        } else {
          this.portfolioFilterCleared.emit(remainingFilterCfgs);
        }
      } else {
        this.clearAllFilter();
      }
    }
  }
  onSubPortfolioChange(
    value: any,
    field: string,
    filterFieldsCfgs: any,
    include?: string,
    isMultiSelect?: boolean
  ) {
    const ids = (value ? (Array.isArray(value) ? value : [value]) : [])
      .filter((item) => item?.id !== null && item?.id !== undefined)
      .map((item) => item.id);
    if (ids.length > 0) {
      this.filterService
        .getFillOptions(`sub_portfolio_id=${ids}`, include, this.moduleName)
        .subscribe((options: any) => {
          let fieldsToChange = ["portfolio", "sub_portfolios", "programs"];

          fieldsToChange = this.modifyFieldsToChange(
            fieldsToChange,
            filterFieldsCfgs
          );
          if (isMultiSelect && fieldsToChange.length >= 2) {
            fieldsToChange = fieldsToChange.slice(2);
          }
          this.changeOptionsAndSetValues(
            fieldsToChange,
            options,
            filterFieldsCfgs
          );
          if (!isMultiSelect) this.clearFilters(["program", "project"]);

          this.filterService.setFilterValue(this.filterCfg);
        });
    } else {
      const portfolioFieldCfg = this.filterCfg.inputs.find(
        (ele: any) => ele.field === "portfolio"
      );
      const value = portfolioFieldCfg.value;
      const allFieldsCfgs = this.getAllFieldsFromFilterCfg();
      if (isMultiSelect)
        this.onPortfolioChange(
          value,
          "portfolio",
          allFieldsCfgs.slice(1),
          include,
          isMultiSelect
        );
      else
        this.onPortfolioChange(
          value,
          "portfolio",
          allFieldsCfgs.slice(1),
          include
        );
    }
  }
  onProgramChange(
    value: any,
    field: string,
    filterFieldsCfgs: any,
    include?: string,
    isMultiSelect?: boolean
  ) {
    const ids = (value ? (Array.isArray(value) ? value : [value]) : [])
      .filter((item) => item?.id !== null && item?.id !== undefined)
      .map((item) => item.id);
    if (ids.length > 0) {
      this.filterService
        .getFillOptions(`program_id=${ids}`, include, this.moduleName)
        .subscribe((options: any) => {
          let fieldsToChange = ["portfolio", "sub_portfolio", "programs"];
          fieldsToChange = this.modifyFieldsToChange(
            fieldsToChange,
            filterFieldsCfgs
          );
          if (isMultiSelect && fieldsToChange.length >= 3)
            fieldsToChange = fieldsToChange.slice(3);
          this.changeOptionsAndSetValues(
            fieldsToChange,
            options,
            filterFieldsCfgs
          );
          if (!isMultiSelect) this.clearFilters(["project"]);
          this.filterService.setFilterValue(this.filterCfg);
        });
    } else {
      const subportfolioFieldCfg = this.filterCfg.inputs.find(
        (ele: any) => ele.field === "subportfolio"
      );
      const value = subportfolioFieldCfg.value;
      const allFieldsCfgs = this.getAllFieldsFromFilterCfg();
      if (!isMultiSelect)
        this.onSubPortfolioChange(
          value,
          "subportfolio",
          allFieldsCfgs,
          include
        );
      else
        this.onSubPortfolioChange(
          value,
          "subportfolio",
          allFieldsCfgs.slice(2),
          include,
          isMultiSelect
        );
    }
  }
  onProjectChange(
    value: any,
    field: string,
    filterFieldsCfgs: any,
    include?: string,
    isMultiSelect?: boolean
  ) {
    const ids = (value ? (Array.isArray(value) ? value : [value]) : [])
      .filter((item) => item?.id !== null && item?.id !== undefined)
      .map((item) => item.id);
    if (ids.length > 0) {
      this.filterService
        .getFillOptions(`project_id=${ids}`, include, this.moduleName)
        .subscribe((options: any) => {
          const fieldsToChange = this.modifyFieldsToChange(
            ["portfolio", "sub_portfolio", "program"],
            filterFieldsCfgs
          );

          this.changeOptionsAndSetValues(
            fieldsToChange,
            options,
            filterFieldsCfgs
          );
          this.filterService.setFilterValue(this.filterCfg);
        });
    } else {
      const programFieldCfg = this.filterCfg.inputs.find(
        (ele: any) => ele.field === "program"
      );
      const value = programFieldCfg.value;
      const allFieldsCfgs = this.getAllFieldsFromFilterCfg();
      if (!isMultiSelect)
        this.onProgramChange(value, "program", allFieldsCfgs, include);
      else
        this.onProgramChange(
          value,
          "program",
          allFieldsCfgs.slice(3),
          include,
          true
        );
    }
  }

  handleSearchableMultiSelectValue(value: any, field: string) {
    const currentValue = value.currentValue?.id;
    const inputItem = this.filterCfg.inputs.find(
      (item: any) => item.field === field
    );
    inputItem.value = value.values;
    const autoFillFields = ["portfolio", "subportfolio", "program", "project"];
    this.isFilterhasValue = this.checkFilterHasValues();
    this.recheckHiddenActiveFilters();
    if (this.autoFillHierarchy && autoFillFields.includes(field)) {
      const allFields = this.getAllFieldsFromFilterCfg();
      if (field === "portfolio") {
        this.onPortfolioChange(
          value.values,
          field,
          allFields.slice(1),
          this.include,
          true
        );
      } else if (field === "subportfolio") {
        this.onSubPortfolioChange(
          value.values,
          field,
          allFields.slice(2),
          this.include,
          true
        );
      } else if (field === "program") {
        this.onProgramChange(
          value.values,
          field,
          allFields.slice(3),
          this.include,
          true
        );
      } else if (field === "project") {
        this.filterService.setFilterValue(this.filterCfg);
      }
    } else {
      if (field === "portfolio") {
        this.portfolioChange.emit(currentValue);
      } else if (field === "subportfolio") {
        this.subportfolioChange.emit(currentValue);
      } else if (field === "program") {
        const subportfolioField = this.filterCfg.inputs.find(
          (item: any) => item.field === "subportfolio"
        );
        const subportfolioValues = subportfolioField.value;
        let currentSubportfolioValue = null;
        if (subportfolioValues.length) {
          currentSubportfolioValue =
            subportfolioValues[subportfolioValues.length - 1].id;
        }
        this.programChange.emit({ currentValue, currentSubportfolioValue });
      } else if (field === "project") {
        this.projectChange.emit(currentValue);
      } else {
        this.loggingService.warn(this.warningText);
      }
      this.filterService.setFilterValue(this.filterCfg);
    }
  }

  manipulateSearchableMultiSelect(val: any, field: string) {
    const searchableMultiSelect: any = this.searchableMultiSelects?.find(
      (child: any) => child.controlName === field
    );
    searchableMultiSelect?.manipulateValue?.(val);
  }

  manipulateSearchableSelect(val: any, field: string) {
    const searchableSelect: any = this.searchableSelects?.find(
      (child: any) => child.controlName === field
    );
    searchableSelect?.manipulateValue?.(val);
  }

  manipulateSearchableSelectWithoutEmitting(val: any, field: string) {
    const searchableSelect: any = this.searchableSelects?.find(
      (child: any) => child.controlName === field
    );
    searchableSelect?.manipulateValueWithoutEmitting?.(val);
  }

  manipulateSearchableMultiSelectWithoutEmitting(val: any, field: string) {
    const searchableMultiSelect: any = this.searchableMultiSelects?.find(
      (child: any) => child.controlName === field
    );
    searchableMultiSelect?.manipulateValueWithoutEmitting?.(val);
  }

  getAllFieldsFromFilterCfg() {
    const portfolioFieldCfg = this.filterCfg.inputs.find(
      (ele: any) => ele.field === "portfolio"
    );
    const subportfolioFieldCfg = this.filterCfg.inputs.find(
      (ele: any) => ele.field === "subportfolio"
    );
    const programFieldCfg = this.filterCfg.inputs.find(
      (ele: any) => ele.field === "program"
    );
    const projectFieldCfg = this.filterCfg.inputs.find(
      (ele: any) => ele.field === "project"
    );
    const opportunityFieldCfg = this.filterCfg.inputs.find(
      (ele: any) => ele.field === "opportunity"
    );

    let Cfgs: any[] = [];
    if (portfolioFieldCfg) Cfgs.push(portfolioFieldCfg);
    if (subportfolioFieldCfg) Cfgs.push(subportfolioFieldCfg);
    if (programFieldCfg) Cfgs.push(programFieldCfg);
    if (projectFieldCfg) Cfgs.push(projectFieldCfg);
    if (opportunityFieldCfg) Cfgs.push(opportunityFieldCfg);
    return Cfgs;
  }

  handleSearchableSelectValue(value: any, field: string) {
    const inputItem = this.filterCfg.inputs.find(
      (item: any) => item.field === field
    );
    inputItem.value = value;
    const val = value?.id;
    const autoFillFields = ["portfolio", "subportfolio", "program", "project"];
    this.isFilterhasValue = this.checkFilterHasValues();
    this.recheckHiddenActiveFilters();
    if (this.autoFillHierarchy && autoFillFields.includes(field)) {
      const allFields = this.getAllFieldsFromFilterCfg();
      if (field === "portfolio") {
        this.onPortfolioChange(value, field, allFields.slice(1), this.include);
      } else if (field === "subportfolio") {
        this.onSubPortfolioChange(value, field, allFields, this.include);
      } else if (field === "program") {
        this.onProgramChange(value, field, allFields, this.include);
      } else if (field === "project") {
        this.onProjectChange(value, field, allFields, this.include);
      }
    } else {
      if (field === "portfolio") {
        this.portfolioChange.emit(val);
      } else if (field === "subportfolio") {
        this.subportfolioChange.emit(val);
      } else if (field === "program") {
        const subportfolioField = this.filterCfg.inputs.find(
          (item: any) => item.field === "subportfolio"
        );
        const subportfolioValue = subportfolioField.value?.id;
        this.programChange.emit({ val, subportfolioValue });
      } else if (field === "project") {
        this.projectChange.emit(val);
      } else {
        this.loggingService.warn(this.warningText);
      }
      this.filterService.setFilterValue(this.filterCfg);
    }
  }

  handleInputOrDateValue(value: any, field: string) {
    const inputItem = this.filterCfg.inputs.find(
      (item: any) => item.field === field
    );
    inputItem.value = value;
    this.isFilterhasValue = this.checkFilterHasValues();
    this.recheckHiddenActiveFilters();
    this.filterService.setFilterValue(this.filterCfg);
  }

  clearAllFilter() {
    this.isFilterhasValue = false;
    this.filterCfg.inputs.forEach((element: any) => {
      if (element.type === "searchable-multiselect") {
        element.value = [];
      } else {
        element.value = null;
      }
    });
    this.searchableMultiSelects.forEach((multiSelect) => {
      multiSelect.selectedValues = [];
    });
    this.searchableSelects.forEach((searchableSelect) => {
      searchableSelect.selectedValue = null;
    });
    this.datepickers.forEach((datepicker) => {
      datepicker.selectedDate = null;
    });
    this.inputs.forEach((input) => {
      input.value = null;
    });
    this.hiddenActiveFilterCount = 0;
    this.hiddenActiveFilterNames = [];
    setTimeout(() => this.calculateRows(), 50);
    this.filterService.setFilterValue(this.filterCfg);
    this.resetFilter.emit("filterValuesCleared");
  }

  getDisplayLables() {
    this.displayLabels = this.commonService.getLabels();
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    clearTimeout(this.transitionTimeout);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
